import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePageTitle } from "@/lib/seo";
import { Card, Button, Pill, Modal, Input, Select } from "@/components/ui/Primitives";
import { Plus, Wallet, AlertTriangle, CheckCircle2, Upload, Filter, ArrowRight } from "lucide-react";
import { confirmDialog } from "@/components/ConfirmDialog";

const CATEGORY_OPTIONS = [
  { key: "repairs", label: "Repairs & maintenance" },
  { key: "utilities", label: "Utilities" },
  { key: "insurance", label: "Insurance" },
  { key: "property_tax", label: "Property tax" },
  { key: "mortgage", label: "Mortgage / interest" },
  { key: "management", label: "Property management" },
  { key: "cleaning", label: "Cleaning" },
  { key: "landscaping", label: "Landscaping & snow" },
  { key: "supplies", label: "Supplies" },
  { key: "professional", label: "Professional fees" },
  { key: "advertising", label: "Advertising" },
  { key: "other", label: "Other" },
];

const STATUS_TONES = {
  draft: "slate", open: "amber", paid: "emerald", overdue: "rose", void: "slate",
};

const fmtCAD = (cents) => `CA$${((cents || 0) / 100).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Bills() {
  usePageTitle("Bills · Accounts Payable — Central Rentals");
  const [bills, setBills] = useState([]);
  const [aging, setAging] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState({
    vendor_id: "", vendor_name: "", property_id: "", amount_dollars: "",
    category: "repairs", invoice_number: "", invoice_date: "", due_date: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = filter ? `?status=${filter}` : "";
      const [b, a, v, p] = await Promise.all([
        api.get(`/accounting/bills${q}`),
        api.get(`/accounting/ap-aging`),
        api.get(`/my-vendors`).catch(() => ({ data: [] })),
        api.get(`/properties`).catch(() => ({ data: { properties: [] } })),
      ]);
      setBills(b.data?.items || []);
      setAging(a.data);
      setVendors(Array.isArray(v.data) ? v.data : (v.data?.items || []));
      setProperties(p.data?.properties || p.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const submitNew = async (e) => {
    e.preventDefault();
    if (!form.vendor_name && !form.vendor_id) { toast.error("Pick a vendor or type a name"); return; }
    if (!form.amount_dollars) { toast.error("Enter an amount"); return; }
    setSaving(true);
    try {
      const payload = {
        vendor_id: form.vendor_id || null,
        vendor_name: form.vendor_name || vendors.find(v => (v.vendor_id || v.id) === form.vendor_id)?.name || null,
        property_id: form.property_id || null,
        amount_cents: Math.round(Number(form.amount_dollars) * 100),
        category: form.category,
        invoice_number: form.invoice_number || null,
        invoice_date: form.invoice_date || null,
        due_date: form.due_date || null,
        notes: form.notes || null,
      };
      await api.post("/accounting/bills", payload);
      toast.success("Bill added.");
      setShowNew(false);
      setForm({
        vendor_id: "", vendor_name: "", property_id: "", amount_dollars: "",
        category: "repairs", invoice_number: "", invoice_date: "", due_date: "", notes: "",
      });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create bill");
    } finally { setSaving(false); }
  };

  const markPaid = async (bill) => {
    try {
      await api.post(`/accounting/bills/${bill.bill_id}/pay`, { method: "e_transfer" });
      toast.success("Marked paid + logged as expense");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark paid");
    }
  };

  const deleteBill = async (bill) => {
    const ok = await confirmDialog({
      title: "Delete this bill?",
      description: `Bill from ${bill.vendor_name} for ${fmtCAD(bill.amount_cents)} will be removed.\n\nThis cannot be undone.`,
      confirmLabel: "Yes, delete",
      cancelLabel: "Keep bill",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/accounting/bills/${bill.bill_id}`);
      toast.success("Bill deleted");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const totals = useMemo(() => {
    const out = bills.reduce((n, b) => n + (b.amount_cents - (b.amount_paid_cents || 0)), 0);
    return { outstanding: out, count: bills.length };
  }, [bills]);

  return (
    <div className="space-y-6" data-testid="bills-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-emerald-700 mb-1">Accounts Payable</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Bills</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Track vendor invoices, mark payments, and keep your P&amp;L accurate. Paid bills flow into your books
            automatically.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/bank-recon" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="bank-recon-link">
            <Upload className="w-4 h-4" /> Bank reconciliation
          </Link>
          <Button onClick={() => setShowNew(true)} data-testid="new-bill-button">
            <Plus className="w-4 h-4 mr-1" /> New bill
          </Button>
        </div>
      </div>

      {/* Aging widget */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ap-aging-widget">
        <AgingCard label="Current" cents={aging?.buckets?.current || 0} tone="slate" />
        <AgingCard label="0–30 days" cents={aging?.buckets?.["0_30"] || 0} tone="amber" />
        <AgingCard label="31–60" cents={aging?.buckets?.["31_60"] || 0} tone="amber" />
        <AgingCard label="61–90" cents={aging?.buckets?.["61_90"] || 0} tone="rose" />
        <AgingCard label="90+ days" cents={aging?.buckets?.["90_plus"] || 0} tone="rose" strong />
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="bills-filter-row">
        <Filter className="w-4 h-4 text-slate-500" />
        {["", "open", "overdue", "paid", "draft"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setFilter(s)}
            data-testid={`filter-${s || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
              filter === s ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "border-slate-200 text-slate-600 hover:border-slate-300"
            }`}
          >
            {s ? s[0].toUpperCase() + s.slice(1) : "All"}
          </button>
        ))}
        <div className="ml-auto text-xs text-slate-500">
          Outstanding: <strong className="text-slate-900">{fmtCAD(totals.outstanding)}</strong>
        </div>
      </div>

      {/* Bills table */}
      <Card>
        {loading ? (
          <div className="p-6 text-sm text-slate-500">Loading…</div>
        ) : bills.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="bills-empty">
            No bills yet. <button onClick={() => setShowNew(true)} className="text-emerald-700 font-semibold hover:underline">Add your first bill</button>.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase tracking-wider">
                <tr className="border-b border-slate-100">
                  <th className="text-left px-4 py-2.5">Vendor</th>
                  <th className="text-left px-4 py-2.5 hidden md:table-cell">Category</th>
                  <th className="text-left px-4 py-2.5 hidden lg:table-cell">Invoice #</th>
                  <th className="text-right px-4 py-2.5">Amount</th>
                  <th className="text-left px-4 py-2.5 hidden md:table-cell">Due</th>
                  <th className="text-center px-4 py-2.5">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {bills.map((b) => (
                  <tr key={b.bill_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`bill-row-${b.bill_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{b.vendor_name}</div>
                      {b.notes && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{b.notes}</div>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-slate-600">{b.category_label}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-slate-500 font-mono text-xs">{b.invoice_number || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{fmtCAD(b.amount_cents)}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-slate-500 text-xs">{b.due_date || "—"}</td>
                    <td className="px-4 py-3 text-center">
                      <Pill tone={STATUS_TONES[b.status] || "slate"}>{b.status}</Pill>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {b.status !== "paid" && (
                        <button onClick={() => markPaid(b)} data-testid={`pay-bill-${b.bill_id}`} className="text-xs font-semibold text-emerald-700 hover:underline mr-3">
                          Mark paid
                        </button>
                      )}
                      <button onClick={() => setSelected(b)} className="text-xs font-semibold text-slate-600 hover:underline">
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {aging?.top_vendors?.length > 0 && (
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">Top outstanding vendors</div>
          <div className="space-y-2">
            {aging.top_vendors.map((v) => (
              <div key={v.vendor_name} className="flex items-center justify-between text-sm">
                <span className="text-slate-800">{v.vendor_name}</span>
                <span className="font-mono font-semibold text-slate-900">{fmtCAD(v.balance_cents)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* New Bill modal */}
      <Modal open={showNew} onClose={() => setShowNew(false)} title="New bill">
        <form onSubmit={submitNew} className="space-y-4" data-testid="bill-form">
          <div>
            <label className="block text-xs uppercase tracking-wider font-bold text-slate-500 mb-1.5">Vendor</label>
            <select value={form.vendor_id}
              onChange={(e) => setForm({ ...form, vendor_id: e.target.value, vendor_name: "" })}
              data-testid="bill-vendor-select"
              className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm bg-white">
              <option value="">— Type a name below —</option>
              {vendors.map((v) => (
                <option key={v.vendor_id || v.id} value={v.vendor_id || v.id}>
                  {v.name} {v.trade ? `(${v.trade})` : ""}
                </option>
              ))}
            </select>
            {!form.vendor_id && (
              <Input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
                     placeholder="Or type a new vendor name (e.g. City of Toronto)"
                     testid="bill-vendor-name-input"/>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Amount (CAD)" type="number" min="0.01" step="0.01" required
                   value={form.amount_dollars} onChange={(e) => setForm({ ...form, amount_dollars: e.target.value })}
                   testid="bill-amount-input"/>
            <Select label="Category" value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    testid="bill-category-select">
              {CATEGORY_OPTIONS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Invoice #" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} testid="bill-invoice-input"/>
            <Input label="Property (optional)" as="select"
                   value={form.property_id}
                   onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                   testid="bill-property-select">
              <option value="">— No specific property —</option>
              {properties.map((p) => (
                <option key={p.property_id} value={p.property_id}>{p.name || p.address}</option>
              ))}
            </Input>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Invoice date" type="date" value={form.invoice_date}
                   onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                   testid="bill-invoice-date-input"/>
            <Input label="Due date" type="date" value={form.due_date}
                   onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                   testid="bill-due-date-input"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-bold text-slate-500 mb-1.5">Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      rows={2} data-testid="bill-notes-input"
                      className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm"
                      placeholder="Any details you want on file…"/>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="bill-submit">
              {saving ? "Saving…" : "Add bill"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Bill detail */}
      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected ? `Bill · ${selected.vendor_name}` : ""}>
        {selected && (
          <div className="space-y-3" data-testid="bill-detail">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Amount">{fmtCAD(selected.amount_cents)}</Field>
              <Field label="Paid">{fmtCAD(selected.amount_paid_cents || 0)}</Field>
              <Field label="Category">{selected.category_label}</Field>
              <Field label="Status"><Pill tone={STATUS_TONES[selected.status]}>{selected.status}</Pill></Field>
              <Field label="Invoice #">{selected.invoice_number || "—"}</Field>
              <Field label="Due date">{selected.due_date || "—"}</Field>
              <Field label="Invoice date">{selected.invoice_date || "—"}</Field>
              {selected.notes && <Field label="Notes" full>{selected.notes}</Field>}
            </div>
            <div className="flex justify-between gap-2 pt-3 border-t border-slate-100">
              <button onClick={() => deleteBill(selected)} className="text-xs font-semibold text-rose-600 hover:underline" data-testid="delete-bill-button">
                Delete
              </button>
              {selected.status !== "paid" && (
                <Button onClick={() => markPaid(selected)} data-testid="detail-pay-button">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Mark paid
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function AgingCard({ label, cents, tone, strong }) {
  const border = tone === "rose" ? "border-rose-200 bg-rose-50" : tone === "amber" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-lg border ${border} p-3`} data-testid={`aging-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className={`font-mono font-bold text-slate-900 ${strong ? "text-lg" : "text-base"} mt-1`}>
        {fmtCAD(cents)}
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-0.5">{label}</div>
      <div className="text-sm text-slate-800">{children}</div>
    </div>
  );
}
